import { isLlamaLend } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * Curve LlamaLend — the one lender whose liquidation the generic model
 * describes WRONGLY rather than merely incompletely.
 *
 * A LlamaLend position does not get liquidated at a price. Collateral is
 * spread over `N` price bands inside the market's own AMM (LLAMMA) and is
 * converted into the borrowed token GRADUALLY as the oracle enters that range
 * — with **no penalty** and **reversibly**: if the price recovers, the
 * conversion unwinds. So:
 *
 *  - there is no single liquidation price (`user_prices` returns a RANGE);
 *  - "in liquidation" is not terminal, which every other lender's UI implies;
 *  - the collateral factor is a FUNCTION of `N`, fixed at open (0.991 at N=4
 *    vs 0.886 at N=50) — so the headline LTV is only true at the default N.
 *
 * The `liquidationDiscount` in the market descriptor is the HARD-liquidation
 * bonus, which applies only in the terminal case, not to soft liquidation.
 */
export const llamaLendAdapter: TermAdapter = {
  id: 'llamalend',
  matches: isLlamaLend,
  profileId: () => 'llamalend.soft@v1',
  build: (input) => {
    const ll = (input.market?.llamalend ?? {}) as Record<string, unknown>
    const bandLtv =
      ll.bandLtv && typeof ll.bandLtv === 'object'
        ? (ll.bandLtv as Record<string, number>)
        : undefined
    const hardDiscount =
      typeof ll.liquidationDiscount === 'number'
        ? ll.liquidationDiscount
        : undefined

    // The two rows are genuinely different products here, unlike the pure CDPs:
    //  - COLLATERAL row: `add_collateral` reads the user's band range and
    //    reverts without a loan, and opening takes collateral, debt AND the
    //    band count together — so this side never opens alone.
    //  - BORROWED-TOKEN row: a real ERC-4626 lending vault. Supplying to it is
    //    an ordinary standalone deposit and has nothing to do with a loan.
    // Flagging both `both-legs` would send a plain lender to a joint open.
    const isCollateralRow = input.collateralActive !== false

    return {
      supply: {
        availability: {
          opensWith: isCollateralRow ? 'both-legs' : 'standalone',
        },
      },
      borrow: {
        // `create_loan(collateral, debt, N)` — both legs plus the band count.
        availability: { opensWith: 'both-legs' },
        debtShape: 'accruing',
        liquidation: {
          model: 'soft-band',
          absorber: 'amm',
          // The property that makes this lender different from every other.
          reversible: true,
          trigger: 'price',
          seizure: 'proportional',
          // Soft liquidation itself is penalty-free; the discount below only
          // applies if the position is ever HARD-liquidated.
          penalty: 0,
          penalties: hardDiscount
            ? [
                {
                  id: 'hard',
                  label: 'Hard-liquidation discount',
                  value: hardDiscount,
                  description:
                    'Applies only to a terminal hard liquidation. Ordinary soft liquidation inside the bands carries no penalty at all.',
                },
              ]
            : undefined,
          bandLtv,
          defaultBands:
            typeof ll.defaultBands === 'number' ? ll.defaultBands : undefined,
          /**
           * The band count as an editable TERM, not just a curve to read.
           *
           * `bandLtv` alone cannot drive a control: it is missing on any
           * market whose geometry could not be read. The domain is always
           * known — `MIN_TICKS`/`MAX_TICKS` are 4..50 on both generations — so
           * the control works even where the curve does not. Where the curve
           * IS present it spans the full domain, so a control can show the LTV
           * for every value it offers.
           *
           * `immutableAfterOpen` is what tells the UI to render this read-only
           * on an existing loan: `_add_collateral_borrow` reuses the tick
           * width, so changing N means closing and reopening.
           */
          openParameter: {
            kind: 'llamalend-bands',
            dimension: 'collateralFactor',
            domain: { min: 4, max: 50 },
            default: typeof ll.defaultBands === 'number' ? ll.defaultBands : 10,
            immutableAfterOpen: true,
          },
          badDebt: 'socialized',
        },
        counterparty: { kind: 'pool', solvency: 'overcollateralized' },
      },
      constraints: {
        positionModel: 'account',
        positionIdMeaning:
          'One loan per address per market — positions are address-keyed.',
      },
    }
  },
}
